import {
  cacheKeys,
  Group,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentWorkspace,
  useCustomMutation,
} from '@gripp/shared-logic';
import {
  ColumnData,
  SelectionStatus,
  SlideLayout,
  SlidePanelRegistrar,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AssetComponent, SelectedAssetRow } from './assetComponent';
import { AssetsFilters } from './assetsFilters';
import { AssetsGroupsPicker } from './assetsGroupsPicker';
import {
  AssetsSelectionPanelHeaderActions,
  AssetsSelectionPanelMain,
} from './assetsSelectionPanel';

const ADD_ASSETS_TO_GROUPS = `
  mutation Asset($input: AddAssetsToGroupsInput!) {
    asset {
      addAssetsToGroups(input: $input) {
        success
        message
      }
    }
  }
`;

export type AssetsShellOutletContext = SlidePanelRegistrar & {
  hasSelection: boolean;
  clearSelection: () => void;
};

export const AssetsShell = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { createGroup } = useCreateGroup();
  const workspace = useCurrentWorkspace(true);
  const currentWorkspaceId = workspace?.id;

  const { groups: availableGroups } = useAllWorkspaceGroups(currentWorkspaceId);

  const [rowsSelected, setRowsSelected] = useState<SelectedAssetRow[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [isGroupsPickerOpen, setIsGroupsPickerOpen] = useState(false);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );

  const hasSelection = rowsSelected.length > 0;

  const { mutateAsync: addAssetsToGroups } = useCustomMutation({
    query: ADD_ASSETS_TO_GROUPS,
  });

  useEffect(() => {
    if (!hasSelection) {
      setSelectedGroups([]);
      setIsGroupsPickerOpen(false);
      return;
    }

    const [first, ...rest] = rowsSelected;
    const firstNames = (first.groups ?? [])
      .map((g) => g.name)
      .filter((name): name is string => !!name)
      .sort();

    const allSame = rest.every((row) => {
      const rowNames = (row.groups ?? [])
        .map((g) => g.name)
        .filter((name): name is string => !!name)
        .sort();
      return (
        rowNames.length === firstNames.length &&
        rowNames.every((n, i) => n === firstNames[i])
      );
    });

    if (!allSame) {
      setSelectedGroups([]);
      return;
    }

    const nextSelected = firstNames
      .map((name) => availableGroups.find((g) => g.name === name))
      .filter((g): g is Group => !!g)
      .map((g) => ({ id: g.id, name: g.name }));

    setSelectedGroups(nextSelected);
  }, [rowsSelected, availableGroups, hasSelection]);

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
  }, []);

  const handleGroupsChange = useCallback(
    async (nextGroups: Group[]) => {
      const previousIds = new Set(selectedGroups.map((g) => g.id));
      const added = nextGroups.filter((g) => !previousIds.has(g.id));

      setSelectedGroups(nextGroups);

      if (added.length === 0 || !currentWorkspaceId) return;

      await addAssetsToGroups({
        input: {
          groups: added.map((g) => ({ id: g.id })),
          assetIds: rowsSelectedIds,
          workspaceId: currentWorkspaceId,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('asset').all,
      });
    },
    [
      selectedGroups,
      rowsSelectedIds,
      currentWorkspaceId,
      addAssetsToGroups,
      queryClient,
    ]
  );

  const outletContext = useMemo(
    () => ({ hasSelection, clearSelection }),
    [hasSelection, clearSelection]
  );

  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    mainComponent: (
      <AssetComponent
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
      />
    ),
    headerComponent: <AssetsFilters />,
    disableScroll: true,
  };

  // Fallbacks: SlideLayout uses these when no child route claims the slot.
  // A navigated panel (e.g. /assets/:id, /assets/filters) takes priority so a
  // URL change reflects in the UI instead of staying stuck on the selection
  // view.
  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection || !currentWorkspaceId) return undefined;

    const title =
      rowsSelected.length === 1
        ? t('assets.topBar.assetSelected')
        : t('assets.topBar.assetsSelected', {
            count: rowsSelected.length,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <AssetsSelectionPanelMain
          selectedGroups={selectedGroups}
          onGroupsChange={handleGroupsChange}
          onCreateGroup={createGroup}
          onOpenPicker={() => setIsGroupsPickerOpen(true)}
        />
      ),
      headerComponent: (
        <AssetsSelectionPanelHeaderActions onReset={clearSelection} />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [
    hasSelection,
    currentWorkspaceId,
    rowsSelected,
    selectedGroups,
    handleGroupsChange,
    clearSelection,
    createGroup,
    t,
    i18n,
  ]);

  const selectionGroupsPickerColumn = useMemo<ColumnData | undefined>(() => {
    if (!isGroupsPickerOpen || !hasSelection || !currentWorkspaceId) {
      return undefined;
    }
    return {
      title: 'asset.groups',
      mainComponent: (
        <AssetsGroupsPicker
          selectedGroups={selectedGroups}
          onGroupsChange={handleGroupsChange}
          onCreateGroup={createGroup}
          workspaceId={currentWorkspaceId}
        />
      ),
      showBackButton: true,
      onBackButtonPress: () => setIsGroupsPickerOpen(false),
      disableScroll: true,
    };
  }, [
    isGroupsPickerOpen,
    hasSelection,
    currentWorkspaceId,
    selectedGroups,
    handleGroupsChange,
    createGroup,
  ]);

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={selectionRightColumn}
      thirdColumn={selectionGroupsPickerColumn}
      enableChildRoutes
      outletContext={outletContext}
    />
  );
};
